"use client";
import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from "react";
import { getUser } from "@/services/auth";
import { useTenant } from "./TenantContext";
export type ThemeType = 'light' | 'dark';

interface ThemeContextType {
    theme: ThemeType;
    toggleTheme: () => void;
    setTheme: (theme: ThemeType) => void;
    colors?: any;
    isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    // Initialise from localStorage immediately to avoid flash
    const [theme, setThemeState] = useState<ThemeType>(() => {
        if (typeof window === 'undefined') return 'light';
        const cached = localStorage.getItem('theme-preference');
        if (cached === 'light' || cached === 'dark') return cached as ThemeType;
        return 'light';
    });
    const [isLoading, setIsLoading] = useState(true);
    const { tenantData } = useTenant();

    const applyTheme = useCallback((themeType: ThemeType) => {
        if (typeof window === 'undefined') return;
        document.documentElement.setAttribute('data-theme', themeType);
    }, []);

    // Apply the initial theme on first mount only
    useEffect(() => {
        applyTheme(theme);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync with user/tenant preference once tenantData resolves.
    // `theme` is intentionally excluded from deps — including it would
    // re-run this effect on every theme change and cause an update loop.
    useEffect(() => {
        const user = getUser();
        let selected: ThemeType = theme;
        if (user?.themePreference) {
            selected = user.themePreference;
        } else if (tenantData?.settings?.defaultTheme) {
            selected = tenantData.settings.defaultTheme;
        }
        if (selected !== theme) {
            setThemeState(selected);
            applyTheme(selected);
        }
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantData]);

    // Persist theme to localStorage whenever user changes it
    useEffect(() => {
        if (!isLoading) {
            localStorage.setItem('theme-preference', theme);
            applyTheme(theme);
        }
    }, [theme, isLoading, applyTheme]);

    const saveUserThemePreference = useCallback(async (themePreference: ThemeType) => {
        try {
            const user = getUser();
            if (user) {
                const updatedUser = { ...user, themePreference };
                localStorage.setItem('auth_user', JSON.stringify(updatedUser));
                const { updateUserThemePreference } = await import('@/services/theme');
                await updateUserThemePreference(themePreference);
            }
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    }, []);

    const setTheme = useCallback((newTheme: ThemeType) => {
        document.documentElement.classList.add("theme-transitioning");
        setTimeout(() => {
            document.documentElement.classList.remove("theme-transitioning");
        }, 300);
        setThemeState(newTheme);
        saveUserThemePreference(newTheme);
    }, [saveUserThemePreference])


    const toggleTheme = useCallback(() => {
        setTheme(theme === 'light' ? 'dark' : 'light');
    }, [theme, setTheme]);

    const value = useMemo<ThemeContextType>(
        () => ({ theme, toggleTheme, setTheme, colors: undefined, isLoading }),
        [theme, toggleTheme, setTheme, isLoading]
    );

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
